import { CartAddEvent } from '@theme/events';

class PaintOrderBuilder extends HTMLElement {
  /** @type {Record<string, {id: number, price: number, compareAtPrice: number|null, available: boolean, options: string[], odooId: number|null}>} */
  #variantMap = {};

  /** @type {number} */
  #dekkingPerLiter = 0;

  /** @type {number} */
  #volumeOptionPosition = 0;

  /** @type {number|undefined} */
  #baseOptionPosition;

  /** @type {string} */
  #moneyFormat = '';

  /** @type {object} */
  #translations = {};

  /** @type {object[]} */
  #allVariants = [];

  /** @type {{ name: string, hex: string } | null} */
  #selectedColor = null;

  connectedCallback() {
    const dataEl = this.querySelector('[data-variant-map]');
    if (!dataEl) return;

    const config = JSON.parse(dataEl.textContent || '{}');
    this.#allVariants = config.variants || [];
    this.#dekkingPerLiter = config.dekkingPerLiter || 0;
    this.#moneyFormat = config.moneyFormat || '€{{amount}}';
    this.#volumeOptionPosition = config.volumeOptionPosition ?? 0;
    this.#baseOptionPosition = config.baseOptionPosition;
    this.#translations = config.translations || {};

    this.#buildVariantMap();

    this.addEventListener('click', this.#handleClick.bind(this));
    this.addEventListener('input', this.#handleInput.bind(this));
    this.addEventListener('change', this.#handleChange.bind(this));

    const section = this.closest('.shopify-section') || document;
    section.addEventListener('paint:calculated', this.#onPaintCalculated.bind(this));
    section.addEventListener('paint:colorSelected', this.#onColorSelected.bind(this));
  }

  #buildVariantMap() {
    const selectedBase = this.#getSelectedBase();
    this.#variantMap = {};

    for (const variant of this.#allVariants) {
      const volumeVal = variant.options[this.#volumeOptionPosition];

      if (this.#baseOptionPosition !== undefined) {
        const baseVal = variant.options[this.#baseOptionPosition];
        if (baseVal !== selectedBase) continue;
      }

      this.#variantMap[volumeVal] = variant;
    }

    this.#updateRowVariantData();
  }

  #getSelectedBase() {
    const checked = this.querySelector('input[name^="paint-order-base"]:checked');
    return checked ? checked.value : null;
  }

  #updateRowVariantData() {
    const rows = this.querySelectorAll('.paint-order-builder__row');
    rows.forEach((row) => {
      const volumeValue = row.dataset.volumeValue;
      const variant = this.#variantMap[volumeValue];

      if (!variant) {
        row.setAttribute('hidden', '');
        const input = row.querySelector('[data-qty-input]');
        if (input) input.value = '0';
        return;
      }

      row.removeAttribute('hidden');
      row.dataset.variantId = String(variant.id);
      row.dataset.variantPrice = String(variant.price);
      row.dataset.variantComparePrice = variant.compareAtPrice != null ? String(variant.compareAtPrice) : '';
      row.dataset.variantAvailable = String(variant.available);
      if (variant.odooId != null && variant.odooId !== '') {
        row.dataset.odooId = String(variant.odooId);
      } else {
        delete row.dataset.odooId;
      }

      this.#updateRowCoverage(row, variant);

      const unitPriceEl = row.querySelector('[data-unit-price]');
      if (unitPriceEl) {
        unitPriceEl.textContent = this.#formatMoney(variant.price);
      }

      const perLiterEl = row.querySelector('[data-price-per-liter]');
      if (perLiterEl) {
        const volumeNumeric = parseFloat(row.dataset.volumeNumeric || '0');
        perLiterEl.textContent = volumeNumeric > 0 ? this.#formatPricePerLiter(variant.price / volumeNumeric) : '';
      }

      const comparePriceEl = row.querySelector('[data-compare-price]');
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        if (comparePriceEl) {
          comparePriceEl.textContent = this.#formatMoney(variant.compareAtPrice);
          comparePriceEl.removeAttribute('hidden');
        } else {
          const s = document.createElement('s');
          s.className = 'paint-order-builder__compare-price';
          s.setAttribute('data-compare-price', '');
          s.textContent = this.#formatMoney(variant.compareAtPrice);
          const priceCell = row.querySelector('.paint-order-builder__row-price');
          const unitPrice = priceCell?.querySelector('[data-unit-price]');
          if (priceCell && unitPrice) {
            priceCell.insertBefore(s, unitPrice);
          }
        }
      } else if (comparePriceEl) {
        comparePriceEl.setAttribute('hidden', '');
      }
    });
  }

  /** @param {HTMLElement} row @param {object} variant */
  #updateRowCoverage(row, variant) {
    const coverageEl = row.querySelector('[data-coverage-text]');
    if (!coverageEl) return;
    const volumeNumeric = parseFloat(row.dataset.volumeNumeric || '0');

    if (variant.coverageMin != null && variant.coverageMax != null) {
      const covMin = variant.coverageMin * volumeNumeric;
      const covMax = variant.coverageMax * volumeNumeric;
      row.dataset.coverageMin = String(covMin);
      row.dataset.coverageMax = String(covMax);
      coverageEl.textContent = covMin === covMax
        ? this.#translations.coveragePerSize?.replace('__M2__', this.#formatCoverage(covMin)) || ''
        : this.#translations.coveragePerSizeRange
            ?.replace('__M2_MIN__', this.#formatCoverage(covMin))
            .replace('__M2_MAX__', this.#formatCoverage(covMax)) || '';
    } else {
      delete row.dataset.coverageMin;
      delete row.dataset.coverageMax;
      if (this.#dekkingPerLiter > 0) {
        const m2 = volumeNumeric * this.#dekkingPerLiter;
        coverageEl.textContent = this.#translations.coveragePerSize?.replace('__M2__', this.#formatCoverage(m2)) || '';
      } else {
        coverageEl.textContent = '';
      }
    }
  }

  /** @param {CustomEvent} event */
  #onColorSelected(event) {
    const { name, hex, basis } = event.detail || {};
    // Colors from image-swatch collections (e.g. Beits Hydro) have no hex;
    // the name alone still identifies the color on the cart line.
    this.#selectedColor = name ? { name, hex: hex || '' } : null;

    if (basis && this.#baseOptionPosition !== undefined) {
      const radio = this.querySelector(`input[data-base-value="${CSS.escape(basis)}"]`);
      if (radio) {
        radio.checked = true;
        this.#buildVariantMap();
        this.#updateTotals();
      }
    }
  }

  /** @param {CustomEvent} event */
  #onPaintCalculated(event) {
    const liters = event.detail?.liters;
    if (!liters || liters <= 0) return;

    const distribution = this.#distribute(liters);
    this.#applyDistribution(distribution);
    this.#updateTotals();
  }

  /**
   * Reads the currently purchasable can options (visible rows) with their
   * volume, option value and price in cents.
   * @returns {{size: number, value: string, price: number}[]}
   */
  #getPurchaseOptions() {
    const options = [];
    const rows = this.querySelectorAll('.paint-order-builder__row:not([hidden])');
    rows.forEach((row) => {
      const size = parseFloat(row.dataset.volumeNumeric || '0');
      const value = row.dataset.volumeValue || '';
      const price = parseInt(row.dataset.variantPrice || '0', 10);
      if (size > 0 && value) {
        options.push({ size, value, price: Number.isFinite(price) ? price : 0 });
      }
    });
    return options;
  }

  /**
   * Picks the cheapest combination of cans whose total volume covers the
   * needed liters. Prefers lower price, then fewer cans, then less overshoot.
   * Falls back to a greedy largest-first pass for very large targets or when
   * no price data is available.
   * @param {number} targetLiters
   * @returns {Record<string, number>}
   */
  #distribute(targetLiters) {
    const options = this.#getPurchaseOptions();
    if (options.length === 0 || targetLiters <= 0) return {};

    options.sort((a, b) => b.size - a.size);

    // Work in integer centiliters to avoid floating point issues (e.g. 2.5 L).
    const SCALE = 100;
    const target = Math.max(1, Math.round(targetLiters * SCALE));
    const sizes = options.map((o) => Math.round(o.size * SCALE));
    const largest = Math.max(...sizes);

    // Never overshoot by more than one full largest can: any solution above
    // that could drop a can and still cover the target.
    const maxCover = target + largest;

    // Guard against pathological inputs (huge surface areas): fall back to a
    // greedy largest-first fill for the bulk before optimising the tail.
    if (maxCover > 200000) {
      return this.#greedyDistribute(targetLiters, options);
    }

    const hasPrices = options.some((o) => o.price > 0);

    /** @type {({price: number, cans: number, choice: number}|null)[]} */
    const dp = new Array(maxCover + 1).fill(null);
    dp[0] = { price: 0, cans: 0, choice: -1 };

    for (let v = 1; v <= maxCover; v++) {
      for (let i = 0; i < sizes.length; i++) {
        const from = v - sizes[i];
        if (from < 0 || dp[from] === null) continue;
        const cand = {
          price: dp[from].price + options[i].price,
          cans: dp[from].cans + 1,
          choice: i,
        };
        const current = dp[v];
        if (current === null || this.#isCheaper(cand, current, hasPrices)) {
          dp[v] = cand;
        }
      }
    }

    // Find the best solution among all volumes that cover the target.
    let bestVolume = -1;
    let bestState = null;
    for (let v = target; v <= maxCover; v++) {
      const state = dp[v];
      if (state === null) continue;
      if (
        bestState === null ||
        this.#isCheaper(state, bestState, hasPrices) ||
        (state.price === bestState.price && state.cans === bestState.cans && v < bestVolume)
      ) {
        bestState = state;
        bestVolume = v;
      }
    }

    if (bestState === null) {
      return this.#greedyDistribute(targetLiters, options);
    }

    // Reconstruct the chosen cans by walking the back-pointers.
    const result = {};
    let v = bestVolume;
    while (v > 0 && dp[v] && dp[v].choice >= 0) {
      const i = dp[v].choice;
      result[options[i].value] = (result[options[i].value] || 0) + 1;
      v -= sizes[i];
    }

    return result;
  }

  /**
   * Compares two candidate solutions. Cheaper price wins; when price is
   * unavailable (or tied) fewer cans wins.
   * @param {{price: number, cans: number}} a
   * @param {{price: number, cans: number}} b
   * @param {boolean} hasPrices
   * @returns {boolean} true if a is strictly better than b
   */
  #isCheaper(a, b, hasPrices) {
    if (hasPrices && a.price !== b.price) return a.price < b.price;
    return a.cans < b.cans;
  }

  /**
   * Greedy largest-first fallback covering at least the target liters.
   * @param {number} targetLiters
   * @param {{size: number, value: string}[]} options sorted largest-first
   * @returns {Record<string, number>}
   */
  #greedyDistribute(targetLiters, options) {
    const result = {};
    let remaining = targetLiters;

    for (const { size, value } of options) {
      const qty = Math.floor(remaining / size);
      if (qty > 0) {
        result[value] = qty;
        remaining -= qty * size;
      }
    }

    if (remaining > 0.01) {
      const smallest = options[options.length - 1];
      result[smallest.value] = (result[smallest.value] || 0) + 1;
    }

    return result;
  }

  /** @param {Record<string, number>} distribution */
  #applyDistribution(distribution) {
    const rows = this.querySelectorAll('.paint-order-builder__row');
    rows.forEach((row) => {
      const volumeValue = row.dataset.volumeValue;
      const input = row.querySelector('[data-qty-input]');
      if (input && volumeValue !== undefined) {
        input.value = String(distribution[volumeValue] || 0);
      }
    });
  }

  /** @param {Event} event */
  #handleClick(event) {
    const target = event.target.closest('[data-qty-action]');
    if (target) {
      const row = target.closest('.paint-order-builder__row');
      const input = row?.querySelector('[data-qty-input]');
      if (!input) return;

      const current = parseInt(input.value, 10) || 0;
      const action = target.dataset.qtyAction;

      if (action === 'increase') {
        input.value = String(current + 1);
      } else if (action === 'decrease' && current > 0) {
        input.value = String(current - 1);
      }

      this.#updateTotals();
      return;
    }

    const addToCartBtn = event.target.closest('[data-add-to-cart-bundle]');
    if (addToCartBtn) {
      this.#addToCart();
    }
  }

  /** @param {Event} event */
  #handleInput(event) {
    if (event.target.matches('[data-qty-input]')) {
      const val = parseInt(event.target.value, 10);
      if (isNaN(val) || val < 0) {
        event.target.value = '0';
      }
      this.#updateTotals();
    }
  }

  /** @param {Event} event */
  #handleChange(event) {
    if (event.target.matches('input[name^="paint-order-base"]')) {
      this.#buildVariantMap();
      this.#updateTotals();
    }
  }

  #updateTotals() {
    let totalPrice = 0;
    let totalLiters = 0;
    let totalCoverageMin = 0;
    let totalCoverageMax = 0;
    let hasItems = false;
    let hasCoverageData = false;

    const rows = this.querySelectorAll('.paint-order-builder__row');
    rows.forEach((row) => {
      const input = row.querySelector('[data-qty-input]');
      const qty = parseInt(input?.value || '0', 10);
      const price = parseInt(row.dataset.variantPrice || '0', 10);
      const volumeNumeric = parseFloat(row.dataset.volumeNumeric || '0');

      if (qty > 0) {
        hasItems = true;
        totalPrice += qty * price;
        totalLiters += qty * volumeNumeric;

        const covMin = parseFloat(row.dataset.coverageMin);
        const covMax = parseFloat(row.dataset.coverageMax);
        if (!isNaN(covMin) && !isNaN(covMax)) {
          totalCoverageMin += qty * covMin;
          totalCoverageMax += qty * covMax;
          hasCoverageData = true;
        }
      }
    });

    const totalEl = this.querySelector('[data-total-price]');
    if (totalEl) {
      totalEl.textContent = this.#formatMoney(totalPrice);
    }

    const summaryEl = this.querySelector('[data-coverage-summary]');
    if (summaryEl) {
      if (hasItems && hasCoverageData) {
        if (totalCoverageMin === totalCoverageMax) {
          summaryEl.textContent = this.#translations.coverageSummary?.replace('__M2__', this.#formatCoverage(totalCoverageMin)) || '';
        } else {
          summaryEl.textContent = this.#translations.coverageSummaryRange
            ?.replace('__M2_MIN__', this.#formatCoverage(totalCoverageMin))
            .replace('__M2_MAX__', this.#formatCoverage(totalCoverageMax)) || '';
        }
        summaryEl.removeAttribute('hidden');
      } else if (hasItems && this.#dekkingPerLiter > 0) {
        const coverageM2 = totalLiters * this.#dekkingPerLiter;
        summaryEl.textContent = this.#translations.coverageSummary?.replace('__M2__', this.#formatCoverage(coverageM2)) || '';
        summaryEl.removeAttribute('hidden');
      } else {
        summaryEl.setAttribute('hidden', '');
      }
    }

    const addBtn = this.querySelector('[data-add-to-cart-bundle]');
    if (addBtn) {
      addBtn.disabled = !hasItems;
    }
  }

  async #addToCart() {
    const items = [];
    const rows = this.querySelectorAll('.paint-order-builder__row');
    let totalItemCount = 0;

    rows.forEach((row) => {
      const input = row.querySelector('[data-qty-input]');
      const qty = parseInt(input?.value || '0', 10);
      const variantId = row.dataset.variantId;

      if (qty > 0 && variantId) {
        const item = { id: parseInt(variantId, 10), quantity: qty };
        /** @type {Record<string, string>} */
        const properties = {};
        if (this.#selectedColor) {
          properties['Color'] = this.#selectedColor.name;
          if (this.#selectedColor.hex) {
            properties['_Hex Color'] = this.#selectedColor.hex;
          }
        }
        const odooId = row.dataset.odooId;
        if (odooId) {
          properties['_odoo_id'] = odooId;
        }
        if (Object.keys(properties).length > 0) {
          item.properties = properties;
        }
        items.push(item);
        totalItemCount += qty;
      }
    });

    if (items.length === 0) return;

    const addBtn = this.querySelector('[data-add-to-cart-bundle]');
    const statusEl = this.querySelector('[data-status-message]');

    if (addBtn) addBtn.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const result = await response.json();

      if (result.status) {
        if (statusEl) statusEl.textContent = result.message || this.#translations.addToCartError;
        if (addBtn) addBtn.disabled = false;
        return;
      }

      if (statusEl) {
        statusEl.textContent = this.#translations.addedToCart || '';
        setTimeout(() => { statusEl.textContent = ''; }, 5000);
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.sectionId || '', {
          source: 'paint-order-builder',
          itemCount: totalItemCount,
          productId: this.dataset.productId,
        })
      );
    } catch (error) {
      console.error('Paint order builder: cart add failed', error);
      if (statusEl) statusEl.textContent = this.#translations.addToCartError || 'Error';
    } finally {
      if (addBtn) addBtn.disabled = false;
      this.#updateTotals();
    }
  }

  /**
   * Formats a coverage m² value for display (comma as decimal separator).
   * @param {number} value
   * @returns {string}
   */
  #formatCoverage(value) {
    return value % 1 === 0 ? String(value) : value.toFixed(1).replace('.', ',');
  }

  /**
   * Formats a per-liter price (in cents) using the localized template.
   * @param {number} cents
   * @returns {string}
   */
  #formatPricePerLiter(cents) {
    const template = this.#translations.pricePerLiter || '__PRICE__';
    return template.replace('__PRICE__', this.#formatMoney(Math.round(cents)));
  }

  /**
   * Formats cents into a money string using the shop's money_format.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithDot = (cents / 100).toFixed(2);

    return this.#moneyFormat
      .replace('{{amount_with_comma_separator}}', amount)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amountWithDot);
  }
}

if (!customElements.get('paint-order-builder')) {
  customElements.define('paint-order-builder', PaintOrderBuilder);
}
